import {
  Alert,
  Button,
  Icon,
  IconButton,
  Stack,
  Typography
} from "@mui/material";
import AiIcon from "assets/icons/magic.svg";
import AccordionSection from "common/components/Accordian";
import ActionButton from "common/components/ActionButton";
import ClickableTypography from "common/components/ClickableTypography";
import CodeEditor from "common/components/CodeEditor";
import Fab from "common/components/Fab";
import GlowingDot from "common/components/GlowingDot";
import { MiniLoader } from "common/components/Loader";
import Window from "common/components/Window";
import MultiLevelMenu from "common/components/MultilevelMenu";
import Page from "common/components/Page";
import SidePanel from "common/components/SidePanel";
import Table from "common/components/Table/table";
import useConfirm from "common/hooks/useConfirm";
import useDownloadFile from "common/hooks/useDownloadFile";
import useFetcher from "common/hooks/useFetcher";
import useScopeAttributes from "common/hooks/useScopeAttributes";
import toSnakeCase from "common/utils/createSnakeCase";
import CommandDebugPanel from "pages/Action/components/Execute";
import JobsModal from "pages/SystemDash/component/Jobs";
import React from "react";
import toast from "react-hot-toast";
import ReactMarkdown from "react-markdown";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { PackageIcon } from "routes";
import { themeOptions } from "theme";
import AstroHubPublish from "./components/AstroHubPublish";
import CodeGenerator, { CodeViewer } from "./components/CodeGenerator";
import CommandPackages from "./components/Packages";
const PackageMenu = ({ id, versions }) => {
  const [selectAll, setSelectAll] = React.useState(false);
  const [selectedResources, setSelectedResources] = React.useState(new Set());
  const [unselectedResources, setUnselectedResources] = React.useState(
    new Set()
  );
  const [refreshKey, setRefreshKey] = React.useState(0);
  let [searchParams, setSearchParams] = useSearchParams();
  const fetcher = useFetcher();
  const deletePkg = React.useCallback(async () => {
    let selected = [];
    if (selectAll) {
      selected = versions.filter(
        x => !Array.from(unselectedResources).includes(x) && x !== "0"
      );
    } else {
      selected = Array.from(selectedResources);
    }
    if (!selected.length) {
      return;
    }
    await fetcher.delete(
      "deleting versions",
      `/api/pkg/${id}/versions?versions=${selected.join(",")}`,
      ({ success }) => {
        //clear searchparams uri
        setSearchParams({});
        setSelectAll(false);
        setSelectedResources(new Set());
        setUnselectedResources(new Set());
        setRefreshKey(x => ++x);
      },
      () => {}
    );
  }, [versions, selectAll, selectedResources, unselectedResources]);

  const [openDeleteModal, DeleteModal] = useConfirm({
    title: `Delete Package Versions`,
    message: `Are you sure you want to delete these versions? This action can't be undone.`,
    callback: () => {
      deletePkg();
    }
  });

  return (
    <Stack gap={3}>
      <DeleteModal />
      <Stack gap={3}>
        <Stack gap={1} pb={2}>
          <div style={{ marginLeft: "auto" }}>
            <ActionButton
              icon="delete"
              dark
              noload
              danger
              handler={async () => {
                openDeleteModal(true);
              }}
            >
              Delete Versions
            </ActionButton>
          </div>
          <Table
            key={refreshKey}
            data={
              versions
                ?.filter(v => Number(v) !== 0)
                ?.map(v => ({ id: v, version: v })) || []
            }
            idProperty={"id"}
            columns={[
              {
                headerName: "Version",
                field: "version",
                minWidth: 200,
                resizable: true,
                flex: 1
              }
            ]}
            noPaginate
            tableName={"e1pkgvertable"}
            tableHeight={500}
            selectAll={selectAll}
            setSelectAll={setSelectAll}
            selectedRowIds={selectedResources}
            setSelectedRowIds={setSelectedResources}
            unselectedRowIds={unselectedResources}
            setUnselectedRowIds={setUnselectedResources}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};
export const VersionInfo = ({ docs, packages }) => {
  let documentation = docs?.markdown || "## No Documentation";
  console.log(docs);
  return (
    <Window
      contentStyles={{ padding: "10px", background: "#171717" }}
      title={`Documentation`}
      icon={"code"}
    >
      <Stack style={{ background: "#171717" }}>
        {documentation ? (
          <ReactMarkdown
            components={{
              code: ({ node, inline, className, children, ...props }) => {
                if (inline) {
                  return (
                    <code
                      className={`inline-block px-1 py-0.5 rounded bg-slate-700 text-slate-100`}
                      {...props}
                    >
                      {children}
                    </code>
                  );
                }

                const language = className?.replace("language-", "");
                return (
                  <CodeViewer
                    showLineNumbers={false}
                    code={children}
                    language={"markdown"}
                  />
                );
              }
            }}
            linkTarget="_blank"
            className="react-markdown-custom mardown-documentation"
          >
            {documentation}
          </ReactMarkdown>
        ) : null}

        {packages?.length ? (
          <Window
            contentStyles={{ padding: "10px", background: "#171717" }}
            title={`PyPI Packages`}
            icon={"token"}
          >
            <ul>
              {packages.map((x, i) => {
                return <li key={i}>{x?.pkg}</li>;
              })}
            </ul>
          </Window>
        ) : null}
      </Stack>
    </Window>
  );
};
let AccordianConfig = ({
  docs,
  version,
  data,
  setData,
  fetcher,
  packages,
  setPackagesState,
  openConfirmModal,
  setSearchParams,
  refresh
}) => {
  const navigate = useNavigate();
  const { id } = useParams();
  if (docs && Number(version) !== 0) {
    return <VersionInfo docs={docs} packages={packages} />;
  }
  return (
    <AccordionSection
      defaultSection={"1"}
      sections={[
        ...(version === 0
          ? [
              {
                title: "Testing",
                icon: "terminal",
                content: (() => {
                  return data?.testing ? (
                    <>
                      <Typography variant="body1">
                        Currently configured system command -
                      </Typography>
                      <Stack gap={1}>
                        <Stack
                          gap={1}
                          direction={"row"}
                          alignItems={"center"}
                          sx={{ position: "relative" }}
                        >
                          <Typography sx={{ fontWeight: "bold" }}>
                            {data?.testing?.system_name}
                          </Typography>
                          <Typography sx={{ fontWeight: "bold" }}>
                            / {data?.testing?.cmd_name}
                          </Typography>
                          <IconButton
                            onClick={() =>
                              navigate(
                                `/app/sys/${data?.testing?.system_id}/action/${data?.testing?.cmd_id}?system=testcmdonly&pkg=${id}`
                              )
                            }
                          >
                            <Icon>open_in_new</Icon>
                          </IconButton>
                        </Stack>
                        <div style={{ marginLeft: "auto" }}>
                          <ActionButton
                            icon="clear"
                            danger
                            noload
                            handler={async () => {
                              await fetcher.post(
                                "unlinking command from package",
                                `/api/pkg/${id}/testing`,
                                {},
                                data => {
                                  if (data?.error)
                                    return toast.error(data?.error);
                                },
                                () => {}
                              );
                              refresh();
                            }}
                          >
                            Unlink Command
                          </ActionButton>
                        </div>
                      </Stack>
                    </>
                  ) : (
                    <>
                      <Typography variant="body1">No linked command</Typography>
                    </>
                  );
                })()
              },
              {
                title: "Packages",
                component: <PackageIcon size={28} fill="#ff914c" />,
                content: (
                  <CommandPackages
                    {...{
                      isStaging: version === 0,
                      data,
                      setData,
                      packages,
                      setPackagesState
                    }}
                  />
                )
              },
              {
                title: "Versions",
                icon: "tag",
                content: (
                  <PackageMenu
                    {...{
                      versions: data?.versions || [],
                      id: data?.name
                    }}
                  />
                )
              },
              {
                title: "Management",
                icon: "key",
                content: (() => {
                  const {
                    ScopeInputs,
                    getScope,
                    setInit,
                    loading
                  } = useScopeAttributes();
                  -React.useEffect(() => {
                    setInit(data?.tags || [], data?.acl || []);
                  }, [data, loading]);
                  const updatePkg = async () => {
                    await fetcher.put(
                      "updating package",
                      `/api/pkg/${data?.name}`,
                      { ...getScope() },
                      data => {
                        if (data?.error) return toast.error(data?.error);
                        setSearchParams({});
                      },
                      () => {}
                    );
                  };
                  return (
                    <Stack gap={1} sx={{ minHeight: "100px" }}>
                      {loading ? (
                        <div
                          style={{
                            marginLeft: "20px",
                            position: "relative"
                          }}
                        >
                          <MiniLoader />
                        </div>
                      ) : (
                        <>
                          <ScopeInputs />
                          <div style={{ marginLeft: "auto" }}>
                            <ActionButton
                              icon="save"
                              dark
                              noload
                              handler={async () => {
                                updatePkg();
                              }}
                            >
                              Update
                            </ActionButton>
                          </div>
                        </>
                      )}
                    </Stack>
                  );
                })()
              }
            ]
          : [])
      ]}
    />
  );
};

let SidePanelConfig = ({
  docs,
  setPackagesState,
  setData,
  version,
  data,
  packages,
  setSearchParams,
  fetcher,
  openConfirmModal,
  refresh,
  exportPackage,
  setPublish
}) => (
  <Stack p={2} sx={{ overflowY: "scroll", height: "100%" }}>
    {version === 0 ? (
      <Stack mb={2} direction={"row"} justifyContent={"flex-end"}>
        <ActionButton
          noload
          dark
          icon="publish"
          handler={() => openConfirmModal(true)}
        >
          Publish Version
        </ActionButton>
        <div style={{ position: "relative" }}>
          <MultiLevelMenu
            button={props => (
              <Button {...props}>
                <Icon>more_vert</Icon>
              </Button>
            )}
            menu={{
              main: {
                "Publish to AstroHub": {
                  leftIcon: <Icon>publish</Icon>,
                  onClick: () => {
                    setPublish(true);
                  }
                },
                "Export Package": {
                  leftIcon: <Icon>system_update_alt</Icon>,
                  onClick: () => {
                    exportPackage();
                  }
                }
              }
            }}
          />
        </div>
      </Stack>
    ) : null}
    <AccordianConfig
      {...{
        refresh,
        openConfirmModal,
        docs,
        setPackagesState,
        setData,
        version,
        data,
        packages,
        setSearchParams,
        fetcher
      }}
    />
  </Stack>
);
const Status = ({ children }) => {
  let colormap = {
    building: themeOptions.palette.info.main,
    deployed: themeOptions.palette.success.main,
    error: themeOptions.palette.error.main
  };
  return (
    <Stack alignItems="center" direction="row" gap={1}>
      <GlowingDot style={{ backgroundColor: colormap[children] }} />
      <Typography variant="caption">{children}</Typography>
    </Stack>
  );
};
export default function Package({ role }) {
  const [loading, setLoading] = React.useState(false);
  const [dirty, setDirty] = React.useState(false);
  const [err, setError] = React.useState(false);
  const [message, setMessage] = React.useState(false);
  const [packages, setPackages] = React.useState([]);
  const [menu, setMenu] = React.useState(false);
  const { id } = useParams();
  let [searchParams, setSearchParams] = useSearchParams();
  const [code, setCodeState] = React.useState("");
  const [docs, setDocs] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  const [generator, setGenerator] = React.useState(false);
  const [execute, setExecute] = React.useState(false);
  const [jobsOpen, setJobsOpen] = React.useState(false);
  const [publish, setPublish] = React.useState(false);

  const exportPackage = useDownloadFile({
    name: `echelonone_${toSnakeCase(id)}_package.zip`,
    notify: "exporting package",
    endpoint: `/api/trade/export/package/${id}`
  });
  const [version, setVersion] = React.useState(
    Number(searchParams.get("version") || 0)
  );
  const [data, setData] = React.useState({
    name: "",
    description: "",
    packages: [],
    tags: [],
    acl: [],
    scan: []
  });
  const setPackagesState = d => {
    setPackages(d);
    if (!dirty) setDirty(true);
  };
  const setCode = d => {
    setCodeState(d);
    if (!dirty) setDirty(true);
  };

  const publishVersion = async () => {
    const payload = { packages, code };
    await fetcher.post(
      "publishing package version",
      `/api/pkg/${id}/publish`,
      payload,
      data => {
        if (data?.error) return toast.error(data?.error);
        setDirty(false);
        setData(d => ({
          ...d,
          versions: [...d.versions, data?.version]
        }));
        setVersion(Number(data?.version || 0));
        setSearchParams({ version: Number(data?.version || 0) });
      },
      () => {}
    );
  };
  const [openConfirmModal, UploadModal] = useConfirm({
    title: `Publish Version`,
    message: `Publish a new version of ${data.name} package? `,
    callback: (props, context) => {
      publishVersion();
      setMenu(false);
    },
    onCancel: () => {}
  });

  const fetcher = useFetcher();

  const saveStaging = async () => {
    const payload = { packages, code };
    // if (!validate("command_edit", payload)) return;
    await fetcher.post(
      "saving to package staging",
      `/api/pkg/${id}/staging`,
      payload,
      data => {
        if (data?.error) return toast.error(data?.error);
        setDirty(false);
        fetchPackage();
      },
      () => {}
    );
  };

  const fetchPackage = async () => {
    setMessage(false);
    let ver = searchParams.get("version");
    setLoading(true);
    setDirty(false);
    await fetcher.get(
      "fetching package",
      `/api/pkg/${id}`,
      ({ pkg }) => {
        // setDataBacked(action?.data_backed);
        // setProperties(action?.properties || []);
        // if (isNew) return setError(false);
        // delete action?.data_backed;
        // delete action?.properties;
        // let packages;
        // if (pkg.data) {
        //   packages = pkg.data.packages;
        // } else {
        //   packages = [];
        // }
        setData({ ...pkg });
        // setVersion(data?.version || 0);
        // setSearchParams({ version: data?.version || 0 });
        setCodeState(pkg.code);
        setPackages(pkg.packages);
        // setCodeState(action.code);
        // setOriginalCode(action.code);
        setError(false);
      },
      () => {
        setLoading(false);
        setError(true);
      }
    );
    if (Number(ver) !== 0) {
      await updateCodeBase(ver);
    } else {
      setStatus(null);
      setDocs(null);
    }
    setLoading(false);
  };
  const updateCodeBase = async version => {
    setMessage(false);
    // setVersion(version);
    // setSearchParams({ version: version });
    // setLoading(true);
    // setDirty(false);
    await fetcher.get(
      "fetching version",
      `/api/pkg/${id}/version/${version}`,
      ({ pkg, message }) => {
        // setDataBacked(action?.data_backed);
        // setProperties(action?.properties || []);
        // if (isNew) return setError(false);
        // delete action?.data_backed;
        // delete action?.properties;
        setCodeState(pkg.code);
        setPackages(pkg.packages);
        setDocs(pkg.doc || null);
        setMessage(pkg?.message || false);

        if (pkg.status) {
          setStatus(pkg.status);
        } else {
          setStatus(null);
        }
        // setCodeState(action.code);
        // setOriginalCode(action.code);
      },
      () => {}
    );
  };

  React.useEffect(() => {
    let ver = searchParams.get("version");
    if (!ver) {
      setVersion(0);
      return setSearchParams({ version: 0 });
    }
    fetchPackage();
  }, [searchParams]);

  return (
    <>
      <UploadModal type="info" />
      {data?.testing ? (
        <JobsModal
          system={data?.testing?.system_id}
          open={jobsOpen}
          command={data?.testing?.cmd_id}
          setOpen={setJobsOpen}
        />
      ) : null}
      <Page
        back={`/app/packages`}
        header={
          data?.name ? (
            <Stack direction="row" gap={1}>
              <ClickableTypography
                onClick={v => {
                  if (v === "Staging") {
                    setVersion(Number(0));
                    setSearchParams({ version: "0" });
                    return;
                  }
                  let version = v.replace("v", "");
                  setVersion(Number(version));
                  setSearchParams({ version });
                }}
                sx={{ display: "flex", alignItems: "center" }}
                options={[
                  "Staging",
                  ...(data.versions || []).reduce((a, c) => {
                    if (Number(c) === 0) return a;
                    return [...a, `v${c}`];
                  }, [])
                ]}
                variant="h4"
              >
                <span>{`${data?.name}`}</span>
                <span style={{ marginLeft: "20px", fontSize: "12px" }}>
                  {" "}
                  {`${
                    version === 0 ? "Staging" : `v${version}` || "Staging"
                  }`.toUpperCase()}
                </span>
              </ClickableTypography>
              {status ? <Status>{status}</Status> : null}
            </Stack>
          ) : (
            "Package"
          )
        }
        refresh={() => fetchPackage()}
        dynamicSize
        loading={loading}
        actions={[
          ...[
            ...(`${version}` === "0" && data?.testing
              ? [
                  <ActionButton
                    noload
                    icon="rocket_launch"
                    circle
                    handler={() => {
                      setExecute(true);
                    }}
                  />,
                  <ActionButton
                    noload
                    icon="sms"
                    circle
                    handler={() => setJobsOpen(true)}
                  />
                ]
              : []),
            ...(status !== "building"
              ? [
                  <ActionButton
                    noload
                    circle
                    icon="more_vert"
                    handler={() => setMenu(true)}
                  ></ActionButton>
                ]
              : [])
            // <ActionButton
            //   noload
            //   icon="more_vert"
            //   circle
            //   handler={() => setConfigs(true)}
            // />,
          ]
        ]}
      >
        <SidePanel
          width={600}
          open={!!execute}
          closeDrawer={() => {
            setExecute(null);
            // fetchCommandBase(id, action);
          }}
          props={{
            system: data?.testing?.system_id,
            id: data?.testing?.cmd_id,
            pkg: id
          }}
        >
          {CommandDebugPanel}
        </SidePanel>
        <SidePanel
          width={800}
          open={generator}
          closeDrawer={() => setGenerator(null)}
          polybg
          props={{
            setCode
          }}
        >
          {CodeGenerator}
        </SidePanel>
        <div>
          {Number(version) === 0 ? (
            <>
              <CodeEditor
                parent="app-body"
                name="code-editor"
                value={code}
                setValue={setCode}
                disabled={Number(version) !== 0}
                activeTools={[
                  <IconButton
                    onClick={() => setGenerator(true)}
                    className="cssbuttons-io-button"
                    sx={{
                      alignSelf: "right",
                      padding: 0,
                      margin: 0,
                      height: "40px",
                      width: "40px",
                      backgroundColor: "#a827fa",
                      "&:hover": {
                        backgroundColor: "#bb1fe2"
                      }
                    }}
                  >
                    <img height="24px" width="24px" src={AiIcon} />
                  </IconButton>
                ]}
                passiveTools={(editor, insert) => [
                  {
                    icon: (
                      <IconButton
                        onClick={() => setGenerator(true)}
                        className="cssbuttons-io-button"
                        sx={{
                          alignSelf: "right",
                          padding: 0,
                          margin: 0,
                          height: "40px",
                          width: "40px",
                          backgroundColor: "#a827fa",
                          "&:hover": {
                            backgroundColor: "#bb1fe2"
                          }
                        }}
                      >
                        <img height="24px" width="24px" src={AiIcon} />
                      </IconButton>
                    )
                  },
                  ...(data?.scan?.length ? [] : [])
                ]}
              />
            </>
          ) : (
            <Stack gap={2}>
              {message ? (
                <Alert
                  sx={{
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                    border: "1px solid rgba(255, 255, 255, 0.5)",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                    color: "#eee"
                  }}
                  severity="error"
                >
                  {message}
                </Alert>
              ) : null}
              <CodeViewer code={code} language={"python"} />
            </Stack>
          )}
        </div>
        <Fab
          active={dirty}
          icon="save"
          onClick={() => {
            saveStaging();
          }}
        />
        <SidePanel
          width={700}
          open={publish}
          closeDrawer={() => setPublish(null)}
          props={{ data: data, id }}
        >
          {AstroHubPublish}
        </SidePanel>
        <SidePanel
          width={600}
          open={menu}
          closeDrawer={() => {
            setMenu(false);
          }}
          props={{
            docs,
            setPackagesState,
            setData,
            version,
            data,
            packages,
            fetcher,
            refresh: fetchPackage,
            setSearchParams,
            openConfirmModal,
            exportPackage,
            setPublish
          }}
        >
          {SidePanelConfig}
        </SidePanel>
      </Page>
    </>
  );
}
